import { z } from 'zod';

// The Plan Spec: the shape-discriminated, serializable representation of a
// care-plan template the Plan Builder emits. The agent reasons in this spec —
// never raw FHIR — and these schemas are its structured-output target, so
// every field carries a .describe() the model reads.

// A clinical concept with an optional code. Codes are best-effort, never
// blocking — the agent attaches one only when it grounded it in the org's
// vocabulary (or knows the canonical LOINC/SNOMED/ICD-10 code with confidence).
export const codedConceptSchema = z.object({
  text: z
    .string()
    .describe("Human-readable name, e.g. 'Oswestry Disability Index'."),
  system: z
    .string()
    .optional()
    .describe("Code system URL, e.g. 'http://loinc.org'. Only with `code`."),
  code: z
    .string()
    .optional()
    .describe(
      "The code in `system`, e.g. '74235-9'. Optional — omit unless confident.",
    ),
});

export type CodedConcept = z.infer<typeof codedConceptSchema>;

// A measurable target on a goal: either a single value (compiles to FHIR
// `detailQuantity`) or a low/high range (`detailRange`).
export const goalTargetSchema = z.object({
  measure: codedConceptSchema.describe(
    "What is measured, e.g. 'Pain severity NPRS' (LOINC 72514-3).",
  ),
  value: z
    .number()
    .optional()
    .describe('Single target value (use this OR low/high, not both).'),
  low: z.number().optional().describe('Lower bound of the target range.'),
  high: z.number().optional().describe('Upper bound of the target range.'),
  unit: z
    .string()
    .optional()
    .describe("Human-readable unit, e.g. '%', 'seconds'."),
  comparator: z
    .enum(['<', '<=', '>=', '>'])
    .optional()
    .describe(
      "How the value bounds the target, e.g. '>=' for 'Target ≥ 50°'. Omit for plain equality. Single-value targets only — ranges express bounds via low/high.",
    ),
});

export type GoalTarget = z.infer<typeof goalTargetSchema>;

export const goalSchema = z.object({
  description: z
    .string()
    .describe(
      "The goal in clinical prose, e.g. 'Reduce pain to NPRS ≤ 2 by discharge'.",
    ),
  targets: z
    .array(goalTargetSchema)
    .optional()
    .describe(
      'Measurable targets backing the goal. Omit when the goal is qualitative.',
    ),
});

export type PlanGoal = z.infer<typeof goalSchema>;

// Visit/monitoring cadence, mapped onto FHIR Timing.repeat: `frequency` times
// per `period` `periodUnit`(s). e.g. 2×/week = { frequency: 2, periodUnit: "wk" }.
export const cadenceSchema = z.object({
  frequency: z.number().int().min(1).describe('How many times per period.'),
  period: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe(
      "Length of the period (default 1), e.g. 2 with periodUnit 'wk' = every 2 weeks.",
    ),
  periodUnit: z
    .enum(['d', 'wk', 'mo'])
    .describe('Period unit: d(ay), wk (week), mo(nth).'),
});

export type Cadence = z.infer<typeof cadenceSchema>;

// Machine-readable exercise dosing. All fields optional — populate what was
// prescribed, omit the rest. Compiles to the exercise-dosage extension on the
// ActivityDefinition (default) and the referencing action (occurrence value).
export const activityDosageSchema = z.object({
  sets: z.number().int().min(1).optional().describe('Number of sets, e.g. 3.'),
  reps: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe('Repetitions per set, e.g. 10.'),
  holdSeconds: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe(
      'Hold time per repetition in seconds, e.g. 30 for a 30s isometric hold.',
    ),
  resistance: z
    .string()
    .optional()
    .describe(
      "Resistance/load in clinical shorthand, e.g. 'yellow band', '5 lb', 'bodyweight'.",
    ),
});

export type ActivityDosage = z.infer<typeof activityDosageSchema>;

// A discrete assigned activity — above all a prescribed exercise. Each entry
// compiles to a plan-scoped ActivityDefinition referenced from a child action;
// the SAME name across phases/events means the same exercise (one definition),
// with per-occurrence dosage/frequency.
export const planActivitySchema = z.object({
  name: z
    .string()
    .describe("Activity name, e.g. 'Chin tucks'. Same name = same exercise."),
  code: codedConceptSchema
    .optional()
    .describe(
      'Best-effort coding for the activity — omit unless confident, same policy as measures.',
    ),
  description: z
    .string()
    .optional()
    .describe(
      'How to perform it / its clinical purpose, e.g. setup, form cues.',
    ),
  dosage: activityDosageSchema
    .optional()
    .describe(
      'Structured dosing (sets/reps/hold/resistance). NEVER bake dosing into prose instead.',
    ),
  frequency: cadenceSchema
    .optional()
    .describe(
      "Session frequency, e.g. 2×/day = { frequency: 2, periodUnit: 'd' }.",
    ),
  patientInstructions: z
    .string()
    .optional()
    .describe(
      'Patient-facing instructions in plain language — what a home-program app would display.',
    ),
});

export type PlanActivity = z.infer<typeof planActivitySchema>;

// Layer shared by every shape: identity, the condition addressed, goals.
const sharedFields = {
  title: z
    .string()
    .describe(
      "Plan title, e.g. 'Achilles Tendinopathy — Progressive Loading'.",
    ),
  description: z
    .string()
    .optional()
    .describe(
      'One-paragraph clinical description of the plan and who it is for.',
    ),
  condition: codedConceptSchema
    .optional()
    .describe(
      'The condition this plan addresses (compiles to PlanDefinition.topic).',
    ),
  goals: z
    .array(goalSchema)
    .optional()
    .describe('Plan-level goals with measurable targets.'),
};

// --- Phased: ordered phases, each gated by exit criteria -------------------

export const phaseSchema = z.object({
  title: z
    .string()
    .describe("Phase title, e.g. 'Phase 2 — Progressive Loading'."),
  description: z.string().optional().describe('What happens in this phase.'),
  exitCriteria: z
    .string()
    .optional()
    .describe(
      "Human-readable criteria to advance out of this phase, e.g. 'Pain ≤ 3/10 during single-leg heel raise and ≥ 3 visits completed'.",
    ),
  exitExpression: z
    .string()
    .optional()
    .describe(
      "Optional machine-ish form of the exit criteria (pseudo-FHIRPath over named clinical variables, e.g. 'nprs <= 3 and visitCount >= 3'). Omit if you only have prose.",
    ),
  durationDays: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe(
      'Typical phase duration in days. Omit when the phase is purely goal-gated.',
    ),
  cadence: cadenceSchema
    .optional()
    .describe('Visit cadence during this phase.'),
  activities: z
    .array(planActivitySchema)
    .optional()
    .describe(
      'Discrete assigned activities/exercises prescribed during this phase.',
    ),
});

export type PlanPhaseSpec = z.infer<typeof phaseSchema>;

export const phasedPlanSpecSchema = z.object({
  shape: z
    .literal('phased')
    .describe(
      'Ordered phases with exit criteria; time axis is variable/goal-gated.',
    ),
  ...sharedFields,
  phases: z.array(phaseSchema).min(1).describe('The ordered phase spine.'),
});

export type PhasedPlanSpec = z.infer<typeof phasedPlanSpecSchema>;

// --- Episodic: a baseline punctuated by event-driven responses -------------

export const planEventSchema = z.object({
  name: z
    .string()
    .describe("Event name, e.g. 'Acute flare', 'Fall', 'Symptom escalation'."),
  description: z
    .string()
    .optional()
    .describe('How to recognize this event (what counts as one).'),
  response: z
    .string()
    .describe(
      "What care is delivered when this event occurs, e.g. 'Same-week visit; reassess; temporary load reduction'.",
    ),
  activities: z
    .array(planActivitySchema)
    .optional()
    .describe(
      'Discrete activities/exercises assigned as part of the response to this event.',
    ),
});

export type PlanEventSpec = z.infer<typeof planEventSchema>;

export const episodicPlanSpecSchema = z.object({
  shape: z
    .literal('episodic')
    .describe(
      'A care baseline punctuated by defined events with defined responses; time axis is event-driven.',
    ),
  ...sharedFields,
  baseline: z
    .string()
    .describe(
      "The standing/baseline care between events, e.g. 'Monthly maintenance visit and home program'.",
    ),
  baselineActivities: z
    .array(planActivitySchema)
    .optional()
    .describe(
      'Discrete activities/exercises in the standing baseline (e.g. the home program).',
    ),
  events: z
    .array(planEventSchema)
    .min(1)
    .describe('The event taxonomy with responses.'),
});

export type EpisodicPlanSpec = z.infer<typeof episodicPlanSpecSchema>;

// --- Continuous: open-ended management against control bands ---------------

export const controlBandSchema = z.object({
  measure: codedConceptSchema.describe(
    "The monitored metric, e.g. 'HbA1c' (LOINC 4548-4).",
  ),
  low: z.number().optional().describe('Lower acceptable bound.'),
  high: z.number().optional().describe('Upper acceptable bound.'),
  unit: z
    .string()
    .optional()
    .describe("Human-readable unit, e.g. '%', 'mmHg'."),
});

export type ControlBandSpec = z.infer<typeof controlBandSchema>;

export const continuousPlanSpecSchema = z.object({
  shape: z
    .literal('continuous')
    .describe(
      'Open-ended management: keep metrics inside control bands on a monitoring cadence.',
    ),
  ...sharedFields,
  bands: z
    .array(controlBandSchema)
    .min(1)
    .describe('The control bands — metrics to hold in range.'),
  cadence: cadenceSchema.optional().describe('Monitoring/review cadence.'),
  reviewRule: z
    .string()
    .optional()
    .describe(
      "When to escalate or review the plan, e.g. 'Two consecutive out-of-band readings → schedule review visit'.",
    ),
  activities: z
    .array(planActivitySchema)
    .optional()
    .describe(
      'Standing activities/exercises the patient performs on an ongoing basis.',
    ),
});

export type ContinuousPlanSpec = z.infer<typeof continuousPlanSpecSchema>;

// --- The discriminated union -------------------------------------------------

export const planSpecSchema = z.discriminatedUnion('shape', [
  phasedPlanSpecSchema,
  episodicPlanSpecSchema,
  continuousPlanSpecSchema,
]);

export type PlanSpec = z.infer<typeof planSpecSchema>;

export type PlanShapeCode = PlanSpec['shape'];

export const PLAN_SHAPE_CODES = [
  'phased',
  'episodic',
  'continuous',
] as const satisfies readonly PlanShapeCode[];
